import { BorderBox } from "@/components/atoms/BorderBox";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import { useAddCrmModalSimplifiedContext } from "@/features/crm/components/organisms/AddCrmModalSimplified/context/AddCrmModalSimplifiedContext";
import { CrmComponentResolver } from "@/features/crm_system/components/atoms/CrmComponentResolver";
import { AddCrmModalStepProps } from '@/features/crm_system/lib/_core/crmSystem';
import { parameterFormSchema, ParameterFormSchemaType } from "@/features/parameters/components/organisms/ParameterForm";
import { ParameterForm } from '@/features/parameters/components/organisms/ParameterForm/ParameterForm';
import { Configuration, ConfigurationData } from '@/types/crm/configuration/configuration';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from "react-hook-form";
import { useStep } from "usehooks-ts";

const PARAMETER_STEPS = ['crm', 'project', 'survey', 'question'] as const;

const getParameterStepIndex = (stepName: typeof PARAMETER_STEPS[number]) => {
    return PARAMETER_STEPS.indexOf(stepName) + 1; // +1 because steps start at 1 in useStep
}


export function DefineParametersStepTitle() {
    return "Define Parameters"
}

export function DefineParametersStep({
    form: _form
}: Readonly<AddCrmModalStepProps>) {

    const defaultValues: Configuration = {
        parameter: {
            crm: [],
            project: [],
            survey: [],
            question: [],
        }
    }
    const { setIsCurrentStepValid, setOnNextHandler, setOnBackHandler, setCanGoToNextStep } = useAddCrmModalSimplifiedContext()

    const [internalStep, { setStep: setInternalStep, canGoToNextStep: canGoToNextStepInternal, goToNextStep: goToNextStepInternal, canGoToPrevStep: canGoToPrevStepInternal, goToPrevStep: goToPrevStepInternal, reset: resetInternalStep }] = useStep(4)

    // Update handlers when internal step state changes
    useEffect(() => {
        // Next handler
        setOnNextHandler(() => {
            if (canGoToNextStepInternal) {
                goToNextStepInternal();
                return true;
            }
            return false;
        });

        // Back handler
        setOnBackHandler(() => {
            if (canGoToPrevStepInternal) {
                goToPrevStepInternal();
                return true; // handled internally
            }
            return false; // let parent handle
        });

        setCanGoToNextStep(canGoToNextStepInternal);
    }, [canGoToNextStepInternal, goToNextStepInternal, canGoToPrevStepInternal, goToPrevStepInternal, setOnNextHandler, setOnBackHandler, setCanGoToNextStep]);

    // Cleanup only on unmount - use empty deps to avoid re-running on every render
    useEffect(() => {
        return () => {
            setOnNextHandler(undefined);
            setOnBackHandler(undefined);
            resetInternalStep();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty deps = only runs on mount/unmount


    const form = useForm<ParameterFormSchemaType>({
        resolver: zodResolver(parameterFormSchema),
        defaultValues: defaultValues
    });

    const localFormValues = form.watch();
    useEffect(() => {
        _form.setValue('configuration', localFormValues);
        _form.setValue("configuration_data", resolveOptionsFromFormValue(localFormValues));
    }, [localFormValues, _form]);

    useEffect(() => {
        const errors = _form.formState.errors;
        const configErrors = errors.configuration?.parameter;
        console.log("configErrors")
        console.log(configErrors)
        if (configErrors) {
            if (configErrors.crm) {
                setInternalStep(getParameterStepIndex('crm'));
            } else if (configErrors.project) {
                setInternalStep(getParameterStepIndex('project'));
            } else if (configErrors.survey) {
                setInternalStep(getParameterStepIndex('survey'));
            } else if (configErrors.question) {
                setInternalStep(getParameterStepIndex('question'));
            }
        }
    }, [_form.formState.errors, setInternalStep]);


    useEffect(() => {
        setIsCurrentStepValid(form.formState.isValid);
    }, [form.formState.isValid, setIsCurrentStepValid]);


    return (
        <>
            <div className="mb-2">
                <CrmComponentResolver
                    selectedCrmSystem={_form.watch("crm_system")}
                    crmSystemComponentsMap={{
                        "salesforce": (
                            <BorderBox size='sm' variant='info'>
                                Salesforce parameters are defined based on a particular object's properties. These object properties can be associated at the project, survey, or question level from Collaborate. Please add all the salesforce properties that will allow your Collaborate Project to connect to your Salesforce Page object.
                            </BorderBox>
                        )
                    }}
                    fallback={(
                        <BorderBox size='sm' variant='info'>
                            These parameters might be used during export for easier mapping results, providing additional context etc. These properties can be associated at the project, survey, or question level from Collaborate.
                        </BorderBox>
                    )}
                />
            </div>
            <Tabs defaultValue="1" value={String(internalStep)} onValueChange={(value) => setInternalStep(Number(value))}>
                <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="1" >CRM</TabsTrigger>
                    <TabsTrigger value="2" >Project</TabsTrigger>
                    <TabsTrigger value="3">Survey</TabsTrigger>
                    <TabsTrigger value="4">Question</TabsTrigger>
                </TabsList>

                <TabsContent value="1">
                    <ParameterForm
                        label="CRM"
                        description="Are there data elements associated with this CRM that are needed for all projects?"
                        parameterName={"crm"}
                        form={form}
                    />
                </TabsContent>
                <TabsContent value="2">
                    <ParameterForm
                        label="Project"
                        description="Are there data elements associated with this CRM that are needed for all projects?"
                        parameterName={"project"}
                        form={form}
                    />
                </TabsContent>
                <TabsContent value="3">
                    <ParameterForm
                        label="Surveys"
                        description="Are there data elements associated with this CRM that are needed for all surveys?"
                        parameterName={"survey"}
                        form={form}
                    />
                </TabsContent>
                <TabsContent value="4">
                    <ParameterForm
                        label="Questions"
                        description="Are there data elements associated with this CRM that are needed for all questions?"
                        parameterName={"question"}
                        form={form}
                    />
                </TabsContent>
            </Tabs>
        </>
    )
}

function resolveOptionsFromFormValue(data: ParameterFormSchemaType): ConfigurationData {
    const configurationData: ConfigurationData = {
        parameter: {
            project: {},
            survey: {},
            question: {},
            crm: {}
        }
    };
    for (const paramType of ['crm', 'project', 'survey', 'question'] as const) {
        configurationData.parameter[paramType] = {};
        const params = data.parameter?.[paramType];
        if (params && params.length > 0) {
            configurationData.parameter[paramType] = {};
        }
        for (const param of params || []) {
            if (param.options) {
                configurationData.parameter[paramType][param.id] = param.options;
            }
        }
    }
    return configurationData;
}